import { Link } from "react-router-dom";
import { ArrowLeft, Settings2 } from "lucide-react";

import { COMPANY } from "@/lib/company";
import { openCookieSettings } from "@/lib/privacy";

const LAST_UPDATED = "August 7, 2026";

export default function CookiePolicyPage() {
  return (
    <div className="pt-28 md:pt-32 pb-20 md:pb-28">
      <div className="max-w-[980px] mx-auto px-5 md:px-10">
        <Link to="/" className="btn-ghost link-underline inline-flex">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Home
        </Link>

        <header className="mt-10 pb-10 border-b border-white/10">
          <p className="eyebrow">[ Cookies ]</p>
          <h1 className="hero-headline mt-5">Cookie Policy</h1>
          <p className="body-text mt-5 max-w-3xl">
            This policy explains how {COMPANY.legalName} uses cookies and similar
            browser-storage technologies on this website and how you can control
            optional analytics.
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-brand-gray mt-5">
            Last updated: {LAST_UPDATED}
          </p>
        </header>

        <div className="mt-10 space-y-10">
          <LegalSection title="1. What are cookies and similar technologies?">
            <p>
              Cookies are small pieces of information stored by a website in your
              browser. Similar technologies, including local storage, can also remember
              preferences or identifiers on your device. In this policy, we describe
              both where relevant.
            </p>
          </LegalSection>

          <LegalSection title="2. Consent choices">
            <p>
              Optional analytics are disabled by default. On your first visit, you can
              reject non-essential technologies, accept analytics, or manage your
              choices. Rejecting analytics does not prevent you from using the public
              website.
            </p>
            <p className="mt-3">
              Your choice can be changed later through Cookie Settings in the website
              footer. The site stores the privacy choice itself because that storage is
              necessary to remember whether you accepted or rejected optional analytics.
            </p>
          </LegalSection>

          <LegalSection title="3. Storage used by this website">
            <div className="mt-5 overflow-x-auto border border-white/10">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="bg-white/[0.04] text-brand-white">
                  <tr>
                    <th className="p-4 font-semibold">Name / pattern</th>
                    <th className="p-4 font-semibold">Category</th>
                    <th className="p-4 font-semibold">Purpose</th>
                    <th className="p-4 font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 text-brand-gray">
                  <tr>
                    <td className="p-4 font-mono text-xs text-brand-white">
                      minga_cookie_consent_v1
                    </td>
                    <td className="p-4">Strictly necessary</td>
                    <td className="p-4">
                      Local storage used to remember your cookie and analytics choice.
                    </td>
                    <td className="p-4">180 days</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-mono text-xs text-brand-white">
                      ph_*_posthog
                    </td>
                    <td className="p-4">Analytics</td>
                    <td className="p-4">
                      PostHog analytics storage used to distinguish visits or sessions
                      and understand website usage. It is enabled only after analytics
                      consent.
                    </td>
                    <td className="p-4">
                      Persistent; provider configuration may retain it for approximately
                      12 months.
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 font-mono text-xs text-brand-white">
                      minga.admin.token
                    </td>
                    <td className="p-4">Strictly necessary / administration</td>
                    <td className="p-4">
                      Local storage used only when an authorised administrator signs in
                      to the hidden administration area.
                    </td>
                    <td className="p-4">Until logout or removal from browser storage</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </LegalSection>

          <LegalSection title="4. Analytics provider">
            <p>
              When you accept analytics, the website may load PostHog to understand page
              visits and website interaction. The site configuration keeps analytics off
              before consent and disables session recording in the website analytics
              configuration.
            </p>
            <p className="mt-3">
              PostHog may use device or session identifiers and may process analytics
              data outside your country depending on the configured hosting region and
              provider arrangement.
            </p>
          </LegalSection>

          <LegalSection title="5. Strictly necessary technologies">
            <p>
              Technologies that are strictly necessary for security, administration or
              remembering your privacy preference do not depend on optional analytics
              consent. They are used only for the functionality they support.
            </p>
          </LegalSection>

          <LegalSection title="6. Changing or withdrawing consent">
            <p>
              You can withdraw analytics consent at any time. When analytics is rejected
              or withdrawn, the website stops analytics capture through its own consent
              controls and attempts to remove PostHog persistence previously created by
              this website.
            </p>

            <button
              type="button"
              onClick={openCookieSettings}
              className="btn-primary mt-6"
            >
              <Settings2 className="h-4 w-4" />
              Open Cookie Settings
            </button>
          </LegalSection>

          <LegalSection title="7. Browser controls">
            <p>
              You can also delete or block cookies and site storage through your browser
              settings. Blocking strictly necessary storage may cause some administrative
              or preference features to stop working as intended.
            </p>
          </LegalSection>

          <LegalSection title="8. Changes to this policy">
            <p>
              This policy may be updated if the website adds or removes analytics,
              embedded content or other browser-storage technologies. The date at the top
              of this page identifies the current version.
            </p>
          </LegalSection>

          <LegalSection title="9. Contact">
            <p>
              Questions about privacy or cookies can be sent to{" "}
              <a
                href={`mailto:${COMPANY.email}`}
                className="link-underline text-brand-white hover:text-brand-yellow"
              >
                {COMPANY.email}
              </a>
              .
            </p>
            <p className="mt-3">
              For information about personal-data processing and your rights, see the{" "}
              <Link
                to="/privacy-policy"
                className="link-underline text-brand-white hover:text-brand-yellow"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </LegalSection>
        </div>
      </div>

      <style>{`
        .legal-list {
          list-style: none;
          padding: 0;
          display: grid;
          gap: .7rem;
        }
      `}</style>
    </div>
  );
}

function LegalSection({ title, children }) {
  return (
    <section>
      <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-brand-white">
        {title}
      </h2>
      <div className="body-text mt-4 leading-relaxed">{children}</div>
    </section>
  );
}
